import { useEffect, useRef, useCallback, useState } from "react";

const SAMPLE_RATE = 16000;

/**
 * Manages AudioContext, mic capture worklet, and playback worklet.
 *
 * @param {object} opts
 * @param {boolean} opts.active - Whether audio should be running
 * @param {function} opts.onCapturedAudio - Called with Int16 PCM ArrayBuffer from mic
 * @returns {{ muted, toggleMute, playAudio, clearPlayback }}
 */
export default function useAudio({ active, onCapturedAudio }) {
  const ctxRef = useRef(null);
  const captureNodeRef = useRef(null);
  const playbackNodeRef = useRef(null);
  const streamRef = useRef(null);
  const [muted, setMuted] = useState(false);

  const onCapturedRef = useRef(onCapturedAudio);
  onCapturedRef.current = onCapturedAudio;

  useEffect(() => {
    if (!active) return;

    let cancelled = false;

    async function init() {
      const audioCtx = new AudioContext({ sampleRate: SAMPLE_RATE });
      ctxRef.current = audioCtx;

      // Load worklets
      await audioCtx.audioWorklet.addModule("/audio-capture-worklet.js");
      await audioCtx.audioWorklet.addModule("/audio-playback-worklet.js");

      if (cancelled) {
        audioCtx.close();
        return;
      }

      // Mic capture
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: SAMPLE_RATE,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;

      if (cancelled) {
        stream.getTracks().forEach((t) => t.stop());
        audioCtx.close();
        return;
      }

      const source = audioCtx.createMediaStreamSource(stream);
      const captureNode = new AudioWorkletNode(audioCtx, "audio-capture-processor");
      captureNodeRef.current = captureNode;

      captureNode.port.onmessage = (e) => {
        onCapturedRef.current?.(e.data);
      };

      source.connect(captureNode);
      // Capture node doesn't connect to destination (no local echo)

      // Playback
      const playbackNode = new AudioWorkletNode(audioCtx, "audio-playback-processor");
      playbackNodeRef.current = playbackNode;
      playbackNode.connect(audioCtx.destination);
    }

    init();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      captureNodeRef.current?.disconnect();
      captureNodeRef.current = null;
      playbackNodeRef.current?.disconnect();
      playbackNodeRef.current = null;
      ctxRef.current?.close();
      ctxRef.current = null;
    };
  }, [active]);

  const toggleMute = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;
    const track = stream.getAudioTracks()[0];
    if (track) {
      track.enabled = !track.enabled;
      setMuted(!track.enabled);
    }
  }, []);

  const playAudio = useCallback((pcmBuffer) => {
    playbackNodeRef.current?.port.postMessage(pcmBuffer, [pcmBuffer]);
  }, []);

  const clearPlayback = useCallback(() => {
    playbackNodeRef.current?.port.postMessage("clear");
  }, []);

  return { muted, toggleMute, playAudio, clearPlayback };
}
